import { queryKeys } from '@api/queryKeys';
import { HttpError } from '@api/types';
import {
  DeliveryPoint,
  DetailedDeliveryPoint,
  GooCertificate,
} from '@models/deliveryPoint';
import { useQuery } from '@tanstack/react-query';
import { usePageRefreshOnUnauthorizedError } from '@utils/hooks';
import {
  fetchDeliveryPoint,
  fetchDeliveryPoints,
  fetchGooCertificatesMetadata,
} from './deliveryPoints';

export const useDeliveryPoints = () => {
  const { refreshPage } = usePageRefreshOnUnauthorizedError();

  return useQuery<DeliveryPoint[], HttpError | Error>(
    [queryKeys.deliveryPoints],
    fetchDeliveryPoints,
    {
      onError: refreshPage,
    },
  );
};

export const useDeliveryPoint = (
  deliveryPointId: DeliveryPoint['id'] | undefined,
  groupId: DeliveryPoint['groupId'] | undefined,
  installationId: DeliveryPoint['installationId'] | undefined,
  scope: DeliveryPoint['scope'] | undefined,
) => {
  const { refreshPage } = usePageRefreshOnUnauthorizedError();

  return useQuery<DetailedDeliveryPoint, HttpError | Error>(
    [queryKeys.deliveryPoint, deliveryPointId, groupId, installationId],
    () =>
      fetchDeliveryPoint(deliveryPointId!!, groupId!, installationId!, scope),
    {
      enabled: !!deliveryPointId && !!groupId && !!installationId,
      onError: refreshPage,
    },
  );
};

export const useGooCertificatesMetadata = (
  deliveryPointId: DeliveryPoint['id'] | undefined,
  scope: DeliveryPoint['scope'] | undefined,
) => {
  const { refreshPage } = usePageRefreshOnUnauthorizedError();

  return useQuery<GooCertificate[], HttpError | Error>(
    [queryKeys.gooCertificatesMetadata, deliveryPointId, scope],
    () => fetchGooCertificatesMetadata(deliveryPointId!!, scope),
    {
      enabled: !!deliveryPointId,
      onError: refreshPage,
    },
  );
};
